import { usePathname } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { useModal } from '@/components/hooks/useModal';
import { ButtonGroup } from '@/components/shared/buttons/button-group';
import { SocialMediaShareButtons } from '@/components/shared/buttons/social-media-share-buttons';
import { Divider } from '@/components/shared/element/divider';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Grid } from '@/components/shared/layout/grid';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { generateTabletAndMobileImageSources } from '@/components/shared/media/image/generate-sources';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { Breadcrumb } from '@/components/shared/navigation/breadcrumb';
import {
  formatDateFull,
  removeEmptyTags,
  youtubeURL,
} from '@/components/shared/utility/format';
import { MENU_LCV_ABOUT_NEWS } from '@/contents/lcv/link';
import { styled } from '@/stitches.config';
import { IButton } from '@/types/shared';

const ContentItem = styled('div', {
  iframe: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
  },
  variants: {
    size: {
      normal: {
        gridColumn: '1 / span 12',
        '@md': { gridColumn: '2 / span 10' },
        '@xl': { gridColumn: '3 / span 8' },
      },
      full: {
        gridColumn: '1 / span 12',
        '@xl': { gridColumn: '2 / span 10' },
      },
      medium: {
        gridColumn: '1 / span 12',
        '@md': { gridColumn: '3 / span 8' },
        '@xl': { gridColumn: '4 / span 6' },
      },
      half: {
        '@xl': { gridColumn: '1 / span 6' },
      },
    },
  },
  defaultVariants: {
    size: 'normal',
  },
});

const TitleWrap = styled('div', {
  marginBottom: '1em',
});

const DetailWrap = styled('div', {
  '& table': {
    maxWidth: 716,
    '& tbody': {
      '& tr': {
        '&:nth-child(even)': {
          '& th, & td': {
            backgroundColor: '$gray200',
          },
        },
      },
    },
  },
});

export interface NewsDetailProps {
  children?: React.ReactNode;
  metatag?: {
    title?: string;
    description?: string;
    img?: {
      url?: string;
    };
  };
  category?: {
    name: string;
    url: string;
  };
  imageDetail?: ImageProps;
  title: string;
  beginDate: string;
  excerpt?: string;
  detail_items?: {
    detail_line?: boolean;
    detail_title?: string;
    detail_description?: string;
    detail_image?: ImageProps[];
    detail_image_layout?: string;
  }[];
  detailTitle?: string;
  detail?: string;
  youtubeId?: string;
  galleryTitle?: string;
  gallery?: ImageProps[];
  buttons?: IButton[];
  dataTestSection?: string;
  dataTest?: string;
  dataTrack?: string;
  dataShareTrack?: string;
  backButton?: IButton;
}

const NewsDetailSection: React.FC<NewsDetailProps> = ({
  dataTrack = 'news-detail',
  dataShareTrack = 'news-detail-share',
  dataTestSection = 'section_newsDetail',
  dataTest = 'newsDetail',
  ...props
}) => {
  const [currentURL, setCurrentURL] = useState<string>('');
  const [videoSrc, setVideoSrc] = useState<string>('');
  const { api: modal, component: modalComponent } = useModal({
    type: 'gallery',
  });

  const onClickAlbum = (index: number) => {
    modal.open({ imageItems: props.gallery, activeSlideIndex: index });
  };

  const pathname = usePathname();
  useEffect(() => {
    setCurrentURL(location.href);
    setVideoSrc('');
    props.youtubeId
      ? setVideoSrc(youtubeURL(props.youtubeId))
      : setVideoSrc('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <>
      <Section test={dataTestSection}>
        <Breadcrumb
          items={[
            {
              label: MENU_LCV_ABOUT_NEWS.label,
              url: MENU_LCV_ABOUT_NEWS.url,
            },
            {
              label: props.category?.name,
              url: `${MENU_LCV_ABOUT_NEWS.url}/${props.category?.url}`,
            },
            {
              label: props.title,
            },
          ]}
        />
        <Section as="div" size="lg" test={`${dataTest}`}>
          <Grid
            css={{
              rowGap: '$space-8',
              '@md': { rowGap: '$space-10' },
              '@lg': { rowGap: '$space-12' },
            }}
          >
            {!!(props.beginDate || props.title || props.excerpt) && (
              <Box>
                <Container>
                  <Grid templateColumns="12" columnGap="4">
                    <ContentItem>
                      <Grid rowGap="4">
                        {!!props.beginDate && (
                          <Text
                            as="p"
                            variant="text-gray"
                            data-test={`${dataTest}_beginDate`}
                          >
                            {formatDateFull(props.beginDate)}
                          </Text>
                        )}
                        {!!props.title && (
                          <Text
                            as="h1"
                            size="h4"
                            font="bold"
                            wrap
                            dangerouslySetInnerHTML={{ __html: props.title }}
                            data-test={`${dataTest}_title`}
                          />
                        )}
                        {!!props.excerpt && (
                          <DetailWrap>
                            <Text
                              as="div"
                              size="h5"
                              font="base"
                              wrap
                              dangerouslySetInnerHTML={{
                                __html: removeEmptyTags(props.excerpt),
                              }}
                              data-test={`${dataTest}_shortDescription`}
                            />
                          </DetailWrap>
                        )}
                      </Grid>
                    </ContentItem>
                  </Grid>
                </Container>
              </Box>
            )}
            <Box>
              {props.youtubeId ? (
                <Container size="desktop-only">
                  <Grid templateColumns="12" columnGap="4">
                    <ContentItem size="full">
                      <Box
                        position="relative"
                        test={{
                          dataTest: `${dataTest}_youtube`,
                        }}
                        css={{
                          aspectRatio: '16 / 9',
                        }}
                      >
                        <iframe
                          src={videoSrc}
                          frameBorder="0"
                          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={props.title}
                        />
                      </Box>
                    </ContentItem>
                  </Grid>
                </Container>
              ) : (
                <Container size="desktop-only">
                  <Grid templateColumns="12" columnGap="4">
                    <ContentItem
                      size={
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        props.imageDetail.width > props.imageDetail.height
                          ? 'full'
                          : 'medium'
                      }
                    >
                      <Image
                        priority
                        placeholder="shimmer"
                        src={props.imageDetail?.src as string}
                        alt={props.imageDetail?.alt as string}
                        width={props.imageDetail?.width as number}
                        height={props.imageDetail?.height as number}
                        layout="responsive"
                        sources={[
                          {
                            srcset: props.imageDetail?.src as string,
                            media: { minWidth: 1024 },
                            options: {
                              width:
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                props.imageDetail.width >
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                props.imageDetail.height
                                  ? 1280
                                  : 828,
                            },
                          },
                          ...generateTabletAndMobileImageSources({
                            mobileSrc: props.imageDetail?.src,
                          }),
                        ]}
                      />
                    </ContentItem>
                  </Grid>
                </Container>
              )}
            </Box>
            <Box>
              <Container>
                <Grid
                  templateColumns="12"
                  columnGap="4"
                  rowGap={{ '@initial': '6', '@sm': '8', '@md': '10' }}
                >
                  {!!props.detail_items && props.detail_items.length > 0 && (
                    <React.Fragment>
                      {props.detail_items.map((o, i) => (
                        <React.Fragment key={i}>
                          {!!o.detail_line && (
                            <ContentItem>
                              <Divider />
                            </ContentItem>
                          )}
                          {!!(o.detail_title || o.detail_description) && (
                            <ContentItem>
                              {!!o.detail_title && (
                                <TitleWrap>
                                  <Text
                                    as="h4"
                                    size="h5"
                                    font="bold"
                                    wrap
                                    dangerouslySetInnerHTML={{
                                      __html: o.detail_title,
                                    }}
                                  />
                                </TitleWrap>
                              )}
                              {!!o.detail_description && (
                                <DetailWrap>
                                  <Text
                                    as="div"
                                    size="text-body-1"
                                    wrap
                                    dangerouslySetInnerHTML={{
                                      __html: removeEmptyTags(
                                        o.detail_description
                                      ),
                                    }}
                                  />
                                </DetailWrap>
                              )}
                            </ContentItem>
                          )}
                          {!!o.detail_image &&
                            o.detail_image.length > 0 &&
                            o.detail_image_layout !== '2 column' && (
                              <>
                                {o.detail_image.map((sub, i) => (
                                  <ContentItem
                                    key={i}
                                    size={
                                      sub.width > sub.height
                                        ? 'normal'
                                        : 'medium'
                                    }
                                    css={{ '@lg': { my: '$space-2' } }}
                                  >
                                    <Image
                                      placeholder="shimmer"
                                      src={sub.src}
                                      alt={sub.alt}
                                      width={sub.width}
                                      height={sub.height}
                                      layout="responsive"
                                      sources={[
                                        {
                                          srcset: sub.src,
                                          media: { minWidth: 1024 },
                                          options: {
                                            width: 828,
                                          },
                                        },
                                        ...generateTabletAndMobileImageSources({
                                          mobileSrc: sub.src,
                                        }),
                                      ]}
                                    />
                                  </ContentItem>
                                ))}
                              </>
                            )}
                          {!!o.detail_image &&
                            o.detail_image.length > 1 &&
                            o.detail_image_layout === '2 column' && (
                              <ContentItem
                                size={{ '@initial': 'normal', '@lg': 'full' }}
                                css={{ '@lg': { my: '$space-2' } }}
                              >
                                <Grid
                                  templateColumns={{ '@lg': '2' }}
                                  gap={{
                                    '@initial': '6',
                                    '@sm': '8',
                                    '@lg': '10',
                                  }}
                                >
                                  {o.detail_image.map((sub, i) => (
                                    <Box key={i}>
                                      <Image
                                        placeholder="shimmer"
                                        src={sub.src}
                                        alt={sub.alt}
                                        width={sub.width}
                                        height={sub.height}
                                        layout="responsive"
                                        aspectRatio="3by2"
                                        sources={[
                                          {
                                            srcset: sub.src,
                                            media: { minWidth: 1024 },
                                            options: {
                                              width: 576,
                                              crop: '3:2',
                                            },
                                          },
                                          ...generateTabletAndMobileImageSources(
                                            {
                                              mobileSrc: sub.src,
                                              mobileOptions: {
                                                crop: '3:2',
                                              },
                                            }
                                          ),
                                        ]}
                                      />
                                    </Box>
                                  ))}
                                </Grid>
                              </ContentItem>
                            )}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  )}

                  {!!props.gallery && props.gallery.length > 0 && (
                    <ContentItem>
                      {!!props.galleryTitle && (
                        <TitleWrap>
                          <Text
                            as="h4"
                            size="h5"
                            font="bold"
                            wrap
                            dangerouslySetInnerHTML={{
                              __html: props.galleryTitle,
                            }}
                            data-test={`${dataTest}_galleryTitle`}
                          />
                        </TitleWrap>
                      )}
                      <Grid
                        templateColumns={{ '@initial': '3', '@xs': '4' }}
                        gap={{ '@initial': '3', '@md': '4' }}
                        data-test={`${dataTest}_gallery`}
                      >
                        {props.gallery.map((o, i) => (
                          <Box
                            key={i}
                            onClick={() => onClickAlbum(i)}
                            cursor="pointer"
                          >
                            {!!o.src && (
                              <Image
                                placeholder="shimmer"
                                hoverEffect="zoom"
                                src={o.src}
                                alt={o.alt}
                                width={256}
                                height={256}
                                layout="responsive"
                                aspectRatio="1by1"
                                sources={[
                                  {
                                    srcset: o.src,
                                    media: { minWidth: 768 },
                                    options: { width: 200, crop: '1:1' },
                                  },
                                  {
                                    srcset: o.src,
                                    media: { maxWidth: 767 },
                                    options: { width: 128, crop: '1:1' },
                                  },
                                ]}
                              />
                            )}
                          </Box>
                        ))}
                      </Grid>
                    </ContentItem>
                  )}
                  {!!props.buttons && props.buttons.length > 0 && (
                    <ContentItem>
                      <ButtonGroup
                        buttons={props.buttons}
                        tracking={{
                          dataTrack: dataTrack,
                        }}
                        test={{
                          dataTest: `${dataTest}_button_cta`,
                        }}
                      />
                    </ContentItem>
                  )}
                  <ContentItem>
                    {/* eslint-disable-next-line react/jsx-max-depth */}
                    <SocialMediaShareButtons
                      id="copy-link"
                      title={props.title}
                      url={currentURL}
                      dataTrack={dataShareTrack}
                      dataTest={dataTest}
                    />
                  </ContentItem>
                </Grid>
              </Container>
            </Box>
            {!!props.backButton && !!props.backButton.url && (
              <Box>
                <Container>
                  <ButtonGroup
                    justify="center"
                    buttons={[props.backButton]}
                    variant="button-gray"
                    tracking={{
                      dataTrack: dataTrack,
                    }}
                    test={{
                      dataTest: `${dataTest}_backButton`,
                    }}
                  />
                </Container>
              </Box>
            )}
          </Grid>
        </Section>
      </Section>
      {modalComponent}
    </>
  );
};
NewsDetailSection.displayName = 'NewsDetailSection';
export default NewsDetailSection;
